import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarWave } from 'react-cssfx-loading';
import { AlertMessage, Button, Input } from '../StyledComponents';
import Logo from '../../assets/images/lock.jpg';
// import { useLoginUserMutation } from '../../../redux/public/publicSlice';
import { useLoginUserMutation, useForgotPasswordMutation } from '../../../redux/apiSlice';
import { encrypt } from '../../../encryption/Encryption';

// Simple Modal for public use
function PublicModal({
  open, onClose, title, children,
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button
          type="button"
          className="absolute top-2 right-3 text-gray-500 hover:text-red-500 text-xl font-bold"
          onClick={onClose}
        >
          ×
        </button>
        <h2 className="text-lg font-bold mb-4 text-center text-gray-800">{title}</h2>
        {children}
      </div>
    </div>
  );
}

PublicModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserAuthenticated = sessionStorage.getItem('ans-sms');
  const [sessionMessage, setSessionMessage] = useState(location.state?.warning || '');

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  // Forgot Password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');

  const {
    username,
    password,
  } = formData;
  const [loginUser] = useLoginUserMutation();
  const [forgotPassword] = useForgotPasswordMutation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSessionMessage('');
    }, 5000);
    return () => clearTimeout(timeout);
  }, [sessionMessage]);

  useEffect(() => {
    if (isUserAuthenticated) {
      navigate('/private', { state: { warning: 'You are already logged in!' } });
    }
  }, [isUserAuthenticated, navigate]);

  if (isUserAuthenticated === '') {
    return null;
  }

  const handleChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginUser({
        username,
        password,
      });

      if ('data' in result && result.data) {
        setLoading(false);
        localStorage.setItem('ans-sms', JSON.stringify(result.data));
        const encryptedUserData = encrypt(result.data);
        sessionStorage.setItem('ans-sms', encryptedUserData);
        navigate(
          '/private',
          {
            state: {
              success: 'You have logged in successfully!',
            },
          },
        );
      } else {
        setErrorMessage('Sorry. Incorrect username or password.');
      }
    } catch (error) {
      setErrorMessage('Sorry. Incorrect username or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleShowPassword = () => {
    setShowPassword((prevState) => !prevState);
  };

  // Forgot Password handlers
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    setForgotError('');
    try {
      const result = await forgotPassword({ email: forgotEmail });
      if ('error' in result && result.error && result.error.status === 'FETCH_ERROR') {
        setForgotError('Network error. Please try again.');
      } else {
        setForgotMsg('A reset link has been sent.');
        setForgotEmail('');
      }
    } catch (err) {
      setForgotError('Network error. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <section className="h-screen">
      {loading && (
        <div className="bg-gray-900 bg-opacity-90 p-16 fixed z-50 w-full h-full top-0 left-0 flex flex-col items-center justify-center">
          <BarWave color="#54c5fc" height="2rem" width="8rem" />
        </div>
      )}

      {/* Forgot Password Modal */}
      <PublicModal open={forgotOpen} onClose={() => setForgotOpen(false)} title="Forgot Password?">
        <form onSubmit={handleForgotSubmit} className="flex flex-col gap-3">
          <label htmlFor="forgotEmail" className="text-gray-700 text-sm w-full">
            Enter your email address:
            <input
              id="forgotEmail"
              type="email"
              className="border p-2 rounded focus:outline-none focus:ring w-full mt-2"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
            />
          </label>
          <button
            type="submit"
            className="bg-blue-600 text-white rounded px-4 py-2 mt-2 hover:bg-blue-700 disabled:opacity-60"
            disabled={forgotLoading}
          >
            {forgotLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
          {forgotMsg && <div className="text-green-600 text-sm text-center">{forgotMsg}</div>}
          {forgotError && <div className="text-red-500 text-sm text-center">{forgotError}</div>}
        </form>
      </PublicModal>

      <div className="container h-full px-8 py-24 mx-auto">
        <div className="flex h-full flex-wrap items-center justify-center lg:justify-between">
          {sessionMessage && (
            <AlertMessage alertType="warning" message={sessionMessage} />
          )}
          <div className="w-full md:mb-0 md:w-8/12 lg:w-6/12">
            <img
              src={Logo}
              className="w-full"
              alt="Login Padlock"
            />
          </div>
          <div className="md:w-8/12 lg:ml-6 lg:w-5/12">
            {errorMessage && (
              <div className="w-full px-3 py-2 mt-2 rounded-md border mx-auto text-center bg-[#f8d7da] text-[#721c24] border-[#f5c6cb]">
                <p className="font-extralight">{errorMessage}</p>
              </div>
            )}
            <form onSubmit={handleLogin}>
              <Input
                type="text"
                id="username"
                placeholder="Username"
                value={username}
                handleChange={handleChange}
                required
              />
              <Input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="Password"
                value={password}
                handleChange={handleChange}
                required
              />
              <div className="mb-6 flex items-center justify-between">
                <button
                  type="button"
                  className="text-blue-800 transition duration-150 ease-in-out text-sm hover:text-blue-950"
                  onClick={() => setForgotOpen(true)}
                >
                  Forgot Password?
                </button>
                <button
                  type="button"
                  className="ml-0"
                  onClick={handleShowPassword}
                >
                  <p className="text-xs underline font-semibold hover:text-blue-800">
                    {showPassword ? 'HIDE PASSWORD' : 'SHOW PASSWORD'}
                  </p>
                </button>
              </div>
              <Button $primary type="submit" btnText="Log In" />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;
